import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import StickyHeader from '../../components/ui/StickyHeader';
import BottomTabNavigation from '../../components/ui/BottomTabNavigation';
import UpcomingBirthdaysCard from './components/UpcomingBirthdaysCard';
import RecentActivityFeed from './components/RecentActivityFeed';
import QuickActionsSection from './components/QuickActionsSection';
import ContactStatsWidget from './components/ContactStatsWidget';
import TodaysBirthdayAlert from './components/TodaysBirthdayAlert';

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Mock data for dashboard
  const mockContacts = [
    {
      id: 1,
      name: "Sarah Johnson",
      email: "sarah.johnson@email.com",
      phone: "+1 (555) 123-4567",
      birthday: "2024-08-15",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      age: 28,
      company: "Tech Solutions Inc"
    },
    {
      id: 2,
      name: "Michael Chen",
      email: "michael.chen@email.com",
      phone: "+1 (555) 234-5678",
      birthday: "2024-08-12",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      age: 32,
      company: "Design Studio"
    },
    {
      id: 3,
      name: "Emily Rodriguez",
      email: "emily.rodriguez@email.com",
      phone: "+1 (555) 345-6789",
      birthday: "2024-08-18",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      age: 25,
      company: "Marketing Pro"
    },
    {
      id: 4,
      name: "David Wilson",
      email: "david.wilson@email.com",
      phone: "+1 (555) 456-7890",
      birthday: "2024-08-20",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      age: 35,
      company: "Finance Corp"
    },
    {
      id: 5,
      name: "Lisa Thompson",
      email: "lisa.thompson@email.com",
      phone: "+1 (555) 567-8901",
      birthday: "2024-08-25",
      avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face",
      age: 29,
      company: "Healthcare Plus"
    }
  ];

  const mockActivities = [
    {
      id: 1,
      type: 'birthday_sent',
      title: 'Birthday wishes sent',
      description: 'Sent birthday message to Michael Chen',
      timestamp: new Date(Date.now() - 1800000), // 30 minutes ago
      contact: mockContacts?.[1]
    },
    {
      id: 2,
      type: 'contact_added',
      title: 'New contact added',
      description: 'Added Emily Rodriguez to contacts',
      timestamp: new Date(Date.now() - 3600000), // 1 hour ago
      contact: mockContacts?.[2]
    },
    {
      id: 3,
      type: 'message_sent',
      title: 'Message sent',
      description: 'Sent follow-up message to Sarah Johnson',
      timestamp: new Date(Date.now() - 7200000), // 2 hours ago
      contact: mockContacts?.[0]
    },
    {
      id: 4,
      type: 'template_created',
      title: 'Template created',
      description: 'Created new birthday message template',
      timestamp: new Date(Date.now() - 10800000), // 3 hours ago
    },
    {
      id: 5,
      type: 'contact_updated',
      title: 'Contact updated',
      description: 'Updated David Wilson\'s information',
      timestamp: new Date(Date.now() - 14400000), // 4 hours ago
      contact: mockContacts?.[3]
    },
    {
      id: 6,
      type: 'message_sent',
      title: 'Random contact message',
      description: 'Sent message to Lisa Thompson',
      timestamp: new Date(Date.now() - 18000000), // 5 hours ago
      contact: mockContacts?.[4]
    }
  ];

  // Filter contacts for today's birthdays
  const todaysBirthdays = mockContacts?.filter(contact => {
    const today = new Date();
    const birthday = new Date(contact.birthday);
    return birthday?.getDate() === today?.getDate() && 
           birthday?.getMonth() === today?.getMonth();
  });

  // Filter upcoming birthdays (next 30 days)
  const upcomingBirthdays = mockContacts?.filter(contact => {
    const today = new Date();
    const currentYear = today?.getFullYear();
    const birthday = new Date(contact.birthday);
    const birthdayThisYear = new Date(currentYear, birthday.getMonth(), birthday.getDate());
    
    if (birthdayThisYear < today) {
      birthdayThisYear?.setFullYear(currentYear + 1);
    }
    
    const diffTime = birthdayThisYear - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30 && diffDays >= 0;
  })?.sort((a, b) => {
    const today = new Date();
    const currentYear = today?.getFullYear();
    const aBirthday = new Date(currentYear, new Date(a.birthday).getMonth(), new Date(a.birthday).getDate());
    const bBirthday = new Date(currentYear, new Date(b.birthday).getMonth(), new Date(b.birthday).getDate());
    
    if (aBirthday < today) aBirthday?.setFullYear(currentYear + 1);
    if (bBirthday < today) bBirthday?.setFullYear(currentYear + 1);
    
    return aBirthday - bBirthday;
  });

  // Calculate stats
  const birthdaysThisMonth = mockContacts?.filter(contact => {
    const today = new Date();
    const birthday = new Date(contact.birthday);
    return birthday?.getMonth() === today?.getMonth();
  })?.length;

  const messagesSent = mockActivities?.filter(activity => 
    activity?.type === 'message_sent' || activity?.type === 'birthday_sent'
  )?.length;

  // Pull to refresh functionality
  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsRefreshing(false);
  };

  // Event handlers
  const handleSearchQuery = (query) => {
    setSearchQuery(query);
    if (query?.trim()) {
      navigate('/contact-list', { state: { searchQuery: query } });
    }
  };

  const handleAddContact = () => {
    navigate('/add-edit-contact');
  };

  const handleSettingsClick = () => {
    navigate('/settings');
  };

  const handleViewAllBirthdays = () => {
    navigate('/birthday-calendar');
  };

  const handleViewAllActivity = () => {
    navigate('/message-history');
  };

  const handleQuickAction = (action) => {
    console.log(`Quick action: ${action}`);
  };

  const handleStatClick = (stat) => {
    console.log(`Stat clicked: ${stat}`);
  };

  const handleTabChange = (tab) => {
    console.log(`Tab changed: ${tab?.label}`);
  };

  const handleBirthdayDismiss = () => {
    console.log('Birthday alert dismissed');
  };

  const handleSendBirthdayWishes = (contacts) => {
    console.log('Sending birthday wishes to:', contacts);
  };

  // Notification counts for bottom navigation
  const notificationCounts = {
    birthdays: todaysBirthdays?.length
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar Navigation */}
      <BottomTabNavigation 
        onTabChange={handleTabChange}
        notificationCounts={notificationCounts}
      />
      {/* Main Content */}
      <div className="lg:ml-60">
        {/* Sticky Header */}
        <StickyHeader
          onSearchQuery={handleSearchQuery}
          searchQuery={searchQuery}
          showSearch={true}
          showAddContact={true}
          onAddContact={handleAddContact}
          onSettingsClick={handleSettingsClick}
        />

        {/* Main Content Area */}
        <main className="p-4 lg:p-6 pb-20 lg:pb-6">
          {/* Today's Birthday Alert */}
          <TodaysBirthdayAlert
            todaysBirthdays={todaysBirthdays}
            onDismiss={handleBirthdayDismiss}
            onSendWishes={handleSendBirthdayWishes}
          />

          {/* Pull to Refresh Indicator */}
          {isRefreshing && (
            <div className="text-center py-4">
              <div className="inline-flex items-center space-x-2 text-primary">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                <span className="text-sm">Refreshing...</span>
              </div>
            </div>
          )}

          {/* Dashboard Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Mobile: Full Width, Desktop: 2 columns */}
            <div className="lg:col-span-2 space-y-6">
              {/* Quick Actions */}
              <QuickActionsSection
                onAddContact={handleQuickAction}
                onRandomContact={handleQuickAction}
                onSendBirthdayWishes={handleQuickAction}
                todaysBirthdays={todaysBirthdays?.length}
              />

              {/* Upcoming Birthdays */}
              <UpcomingBirthdaysCard
                upcomingBirthdays={upcomingBirthdays}
                onViewAll={handleViewAllBirthdays}
              />

              {/* Recent Activity - Desktop Only */}
              <div className="hidden lg:block">
                <RecentActivityFeed
                  activities={mockActivities}
                  onViewAll={handleViewAllActivity}
                />
              </div>
            </div>

            {/* Right Column - Mobile: Full Width, Desktop: 1 column */}
            <div className="space-y-6">
              {/* Contact Stats */}
              <ContactStatsWidget
                totalContacts={mockContacts?.length}
                birthdaysThisMonth={birthdaysThisMonth}
                messagesSent={messagesSent}
                onStatClick={handleStatClick}
              />

              {/* Recent Activity - Mobile Only */}
              <div className="lg:hidden">
                <RecentActivityFeed
                  activities={mockActivities}
                  onViewAll={handleViewAllActivity}
                />
              </div>
            </div>
          </div>
        </main>
      </div>
      {/* Mobile Bottom Navigation */}
      <BottomTabNavigation 
        onTabChange={handleTabChange}
        notificationCounts={notificationCounts}
      />
    </div>
  );
};

export default Dashboard;